import {
  type AmplifyError,
  signOut,
  useCognitoIdentityProvider,
} from '@backoffice-frontend/authentication'
import { ENV, Environment } from '@backoffice-frontend/environment'
import {
  MoiaGrid,
  useMediaQuery,
  useTheme,
} from '@backoffice-frontend/patterns'
import { Button, Callout } from '@moia-dev/pace-web'
import { signInWithRedirect } from 'aws-amplify/auth'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PublicAppViewContentLayout } from '../components/PublicAppViewContentLayout'
import { getSsoErrorTranslation } from '../utils/ssoErrorMessageTranslation'
import { CognitoLoginView } from './CognitoLoginView'

type LoginViewProps = {
  isProcessingSSO: boolean
  ssoError: string | null
  title: string
  description?: string
}

export const LoginView = ({
  isProcessingSSO,
  ssoError,
  title,
  description,
}: LoginViewProps) => {
  const [isUsingEmailAndPassword, setIsUsingEmailAndPassword] = useState(false)
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { cognitoIdentityProviderName, loading, refetch } =
    useCognitoIdentityProvider()

  const loginWithSSO = async () => {
    if (ENV === Environment.local || ENV === Environment.test) {
      alert(
        "You are trying to login with SSO on a local or test environment. This is not supported. Either login without SSO or use e.g. setEnvironment('integration') in the browser's console to switch to a different environment.",
      )
      return
    }

    if (!cognitoIdentityProviderName) {
      throw new Error('No Cognito Identity Provider configured for SSO login')
    }

    try {
      await signInWithRedirect({
        provider: { custom: cognitoIdentityProviderName },
        customState: JSON.stringify({ hostname: window.location.hostname }),
      })
    } catch (_error) {
      const error = _error as AmplifyError
      if (error.name === 'UserAlreadyAuthenticatedException') {
        await signOut()
        return
      }
      throw error
    }
  }

  if (isUsingEmailAndPassword) {
    return (
      <CognitoLoginView
        onClose={() => setIsUsingEmailAndPassword(false)}
        title={title}
        description={description}
      />
    )
  }

  return (
    <PublicAppViewContentLayout
      title={title}
      isMobile={isMobile}
      onOrganizationChange={() => refetch()}
      description={description}
    >
      <div
        css={theme => ({
          marginTop: isMobile ? theme.space.Space2 : theme.space.Space6,
        })}
      >
        {isProcessingSSO && !ssoError && (
          <Callout variant="warning">
            {t('loginView_ssoWaitingForSSOProvider')}
          </Callout>
        )}
        {ssoError && (
          <Callout variant="critical" title={t('loginView_generalError')}>
            {getSsoErrorTranslation(ssoError, t)}
          </Callout>
        )}
      </div>
      <MoiaGrid gridGap={isMobile ? 1 : 2} mt={isMobile ? 1 : 3}>
        <Button
          label={t('loginView_ssoLoginWithSSO_button')}
          variant="primary"
          size="default"
          disabled={loading || !cognitoIdentityProviderName}
          onClick={loginWithSSO}
        />
        <Button
          label={t('loginView_emailAndPasswordLoginButton')}
          onClick={() => setIsUsingEmailAndPassword(true)}
          size="default"
          variant="secondary"
        />
      </MoiaGrid>
    </PublicAppViewContentLayout>
  )
}
